<template>
  <div>
    <div
      v-for="date in series.seriesDates"
      :key="date.id"
      v-test="'appointment-series-slot'"
    >
      <!-- Active date: expanded with the form. -->
      <BaseCard
        v-if="date.id === series.activeDateId"
        mb
        :closable="series.seriesDates.length > 1"
        :selected="previewModeActive"
        @close="series.removeDate(date.id)"
      >
        <div :class="$style.row">
          <BaseDateTime
            :modelValue="date.startAt"
            :label="$t('global.date_time')"
            @update:modelValue="series.setDateStartAt(date.id, $event)"
            v-test="'appointment-series-date'"
          />
          <BaseButton
            color="inverted"
            :disabled="!canCheckAvailability"
            @click="onFindAvailabilities(date.id)"
            v-test="'appointment-series-timeslots'"
          >
            {{ $t('appointment.create.find_timeslots') }}
          </BaseButton>
        </div>

        <BaseText
          v-if="series.statusForDate(date.id) === 'unavailable'"
          color="error"
          :mt="0.5"
          v-test="'appointment-series-unavailable'"
        >
          {{ $t('appointment.create.series.unavailable') }}
        </BaseText>
        <BaseText
          v-for="message in series.submitErrors[date.id]"
          :key="message"
          color="error"
          :mt="0.5"
          v-test="'appointment-series-error'"
        >
          {{ message }}
        </BaseText>
      </BaseCard>

      <!-- Other dates: collapsed summary, click to expand. -->
      <BaseCard
        v-else
        mb
        noPadding
      >
        <div
          :class="$style.summary"
          @click="series.setActiveDate(date.id)"
          v-test="'appointment-series-slot-summary'"
        >
          <div :class="$style.summaryMain">
            <BaseText bold>
              {{ filters.dateTimeLong(date.startAt) }}
            </BaseText>
            <BaseIcon
              v-if="hasIssue(date.id)"
              name="alert"
              color="error"
              size="s"
              v-test="'appointment-series-slot-warning'"
            />
          </div>
          <div :class="$style.side">
            <BaseIcon name="edit" />
          </div>
        </div>
      </BaseCard>
    </div>

    <div :class="$style.addSlot">
      <BaseText
        inline
        @click="series.addDate()"
        v-test="'appointment-series-add-slot'"
      >
        + {{ $t('appointment.create.series.add_date') }}
      </BaseText>
    </div>
  </div>
</template>

<script
  setup
  lang="ts"
>
import { storeToRefs } from 'pinia';
import filters from '@/filters';
import { useCreateAppointmentStore } from '@/stores/calendar-create-appointment';
import { useCalendarPreviewStore } from '@/stores/calendar-preview';

const emit = defineEmits<{ openTimeslots: [] }>();

const store = useCreateAppointmentStore();
const series = store.series;
const { canCheckAvailability } = storeToRefs(store);

const { previewModeActive } = storeToRefs(useCalendarPreviewStore());

const hasIssue = (id: string) => {
  const status = series.statusForDate(id);
  return status === 'unavailable' || status === 'error';
};

// Reuse the existing timeslot picker: point it at this date, then open it.
const onFindAvailabilities = (id: string) => {
  series.setActiveDate(id);
  emit('openTimeslots');
};
</script>

<style
  lang="scss"
  module
>
.row {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: $spacing * 0.5;
}

.summary {
  display: flex;
  align-items: center;
  padding: $spacing * 0.5;
  cursor: pointer;
}

// Match the services list: the datetime takes the row, the edit icon sits in a
// fixed 20px column so it lines up with the close (x) on the expanded card.
.summaryMain {
  display: flex;
  align-items: center;
  gap: $spacing * 0.5;
  width: calc(100% - 20px);
}

.side {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
}

.addSlot {
  margin-top: $spacing * 0.5;
}
</style>
